import { Box, Typography, Paper, Grid } from '@mui/material'
import { TrendingUp, TrendingDown, Event, Sync } from '@mui/icons-material'

const MarketReport = ({ data, city }) => {
  // console.log(`MarketReport data:`, data)
  const months = Object.keys(data.soldPrice.mth)
  const latestMonth = months.pop()
  const prevMonth = months[months.length - 1]

  const listMonths = Object.keys(data.listPrice.mth)
  const latestListMonth = listMonths.pop()

  const [year, month] = latestMonth.split('-')
  const formattedMonth = new Date(year, month - 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  const latestSold = data.soldPrice.mth[latestMonth]
  const latestList = data.listPrice.mth[latestListMonth]
  const latestAboveBelow = data.aboveBelowList.mth[latestMonth] ?? {
    above: 0,
    below: 0,
  }
  const latestDOM = data.daysOnMarket.avg ?? 'N/A'

  const prevAboveBelow = data.aboveBelowList.mth[prevMonth] ?? {
    above: 0,
    below: 0,
  }

  const getMarketIcon = (marketTrend) =>
    marketTrend.includes('Seller') ? (
      <TrendingUp color="success" fontSize="large" />
    ) : (
      <TrendingDown color="error" fontSize="large" />
    )

  const getSeasonalityIcon = (seasonalityInsight) =>
    seasonalityInsight.includes('Spring') ? (
      <Event color="primary" fontSize="large" />
    ) : (
      <Event color="disabled" fontSize="large" />
    )

  const getBehaviorIcon = (buyerSellerShift) =>
    buyerSellerShift.includes('More homes') ? (
      <Sync color="success" fontSize="large" />
    ) : (
      <Sync color="error" fontSize="large" />
    )

  const marketTrend =
    latestAboveBelow.above > latestAboveBelow.below
      ? 'Seller’s market (competitive bidding)'
      : 'Buyer’s market (more price reductions)'
console.log(`latestListMonth:`, latestListMonth)
  const seasonalityInsight =
    latestListMonth.includes('03') || latestListMonth.includes('04') || latestListMonth.includes('05')
      ? 'Spring typically sees more homes selling above list price due to increased demand.'
      : latestListMonth.includes('06') || latestListMonth.includes('07') || latestListMonth.includes('08')
      ? 'Summer remains active, with steady demand and competitive pricing.'
      : latestListMonth.includes('09') || latestListMonth.includes('10') || latestListMonth.includes('11')
      ? 'Fall often brings a balanced market, with motivated buyers and sellers.'
      : 'Winter tends to be slower, with lower competition and potential for better deals.'

  const buyerSellerShift =
    latestAboveBelow.above > prevAboveBelow.above
      ? `More homes are selling above list price compared to last month (${prevAboveBelow.above}% → ${latestAboveBelow.above}%).`
      : `Fewer homes are selling above list price compared to last month (${prevAboveBelow.above}% → ${latestAboveBelow.above}%).`

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <h1 style={{ marginTop: `16px` }}>
          <span className="highlight">{city}</span> Home Prices &
          Market&nbsp;Trends{' '}
          <span style={{ fontSize: `.9rem`, color: `gray` }}>
            {latestMonth}
          </span>
        </h1>
      </Grid>
      <Grid item xs={12}>
        <h2>Overview</h2>
         <p>
          The average sold price in {formattedMonth} was{' '}
          <strong>${latestSold.avg.toLocaleString()}</strong>, with{' '}
          <strong>{latestSold.count} homes sold</strong>.<br />
          The average listing price was{' '}
          <strong>${latestList.avg.toLocaleString()}</strong>.<br />
          Homes sold{' '}
          <strong>
            {latestAboveBelow.above > latestAboveBelow.below
              ? 'above'
              : 'below'}{' '}
            asking price in {latestAboveBelow.above}% of transactions
          </strong>
          .<br />
          The average <strong>days on market</strong> was{' '}
          <strong>{latestDOM} days</strong>.
        </p>
      </Grid>
      <Grid item xs={12} sx={{ mb: 8 }}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={6} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center',
                padding: `1rem 1.5rem 1.5rem`,
                border: `1px solid rgba(0, 0, 0, 0.2)`,
                boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)`,
                borderRadius: `6px`,
              }}
              elevation={3}
            >
              <Box>
                {getMarketIcon(marketTrend)}
                <Typography variant="h6">Market Trends</Typography>
              </Box>
              <Typography variant="body2">{marketTrend}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center',
                padding: `1rem 1.5rem 1.5rem`,
                border: `1px solid rgba(0, 0, 0, 0.2)`,
                boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)`,
                borderRadius: `6px`,
              }}
              elevation={3}
            >
              <Box>
                {getBehaviorIcon(buyerSellerShift)}
                <Typography variant="h6">Behavior Shifts</Typography>
              </Box>
              <Typography variant="body2">{buyerSellerShift}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center',
                padding: `1rem 1.5rem 1.5rem`,
                border: `1px solid rgba(0, 0, 0, 0.2)`,
                boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)`,
                borderRadius: `6px`,
                marginTop: { xs: 5, sm: 0 },
              }}
              elevation={3}
            >
              <Box>
                {getSeasonalityIcon(seasonalityInsight)}
                <Typography variant="h6">Seasonal Insights</Typography>
              </Box>
              <Typography variant="body2">{seasonalityInsight}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default MarketReport
